import { useCallback, useEffect, useRef, useState } from 'react';
import { referralRequest } from '../utils/referrals';

// One referral summary per signed-in user, shared by the dashboard card and the navbar gift so
// moving between pages doesn't refetch. Short-lived: credit and stats change when friends act.
const TTL_MS = 5 * 60 * 1000;
const cache = new Map(); // uid -> { at, data, verified }
const inflight = new Map(); // `${uid}:${verified}` -> Promise (navbar + dashboard card mount together)

async function loadSummary(verified) {
  const config = await referralRequest('config');
  if (!config.enabled) return null;
  // Keep the public offer so the verify prompt can name the reward.
  if (!verified) return { enabled: true, verificationRequired: true, friendCredit: config.friendCredit || null };
  return referralRequest('summary');
}

/**
 * The signed-in user's referral summary ({ enabled, handle, code, friendCredit, creditAvailableUsd, ... }),
 * `{ enabled: true, verificationRequired: true }` before email verification, or null when referrals are off.
 */
export default function useReferralSummary(user, { enabled = true } = {}) {
  const uid = user?.uid || '';
  const verified = !!user?.emailVerified;
  const [state, setState] = useState(() => {
    const hit = uid && cache.get(uid);
    return { uid, data: hit && Date.now() - hit.at < TTL_MS ? hit.data : null };
  });
  useEffect(() => {
    let stopped = false;
    if (!uid || !enabled) return undefined;
    const hit = cache.get(uid);
    if (hit && Date.now() - hit.at < TTL_MS && hit.verified === verified) {
      setState(prev => (prev.uid === uid && prev.data === hit.data ? prev : { uid, data: hit.data }));
      return undefined;
    }
    setState(prev => (prev.uid === uid && prev.data === null ? prev : { uid, data: null }));
    const key = `${uid}:${verified}`;
    let request = inflight.get(key);
    if (!request) {
      request = loadSummary(verified).then(data => {
        cache.set(uid, { at: Date.now(), data, verified });
        return data;
      }).finally(() => inflight.delete(key));
      inflight.set(key, request);
    }
    request.then(data => { if (!stopped) setState({ uid, data }); }).catch(() => {});
    return () => { stopped = true; };
  }, [uid, enabled, verified]);
  const verifiedRef = useRef(verified);
  verifiedRef.current = verified;
  const setData = useCallback((data) => {
    if (uid) cache.set(uid, { at: Date.now(), data, verified: verifiedRef.current });
    setState({ uid, data });
  }, [uid]);
  return { data: state.uid === uid ? state.data : null, setData };
}

/** Test helper. */
export function clearReferralSummaryCache() { cache.clear(); inflight.clear(); }
